import React, { useEffect, useRef, useState } from 'react';
import { useParams } from "react-router-dom";
import { 
  Box,
  Button,
  Flex,
  useToast,
  Heading,
  Text
} from '@chakra-ui/react';
import SignaturePad from 'signature_pad';
import { cqAPI, HOST } from "../../lib/Auth";
import * as pdfjsLib from 'pdfjs-dist/webpack';

const SignDocument = ({ signatureRequestId, onSuccess }) => {
  const signatureCanvasRef = useRef(null);
  const documentCanvasContainerRef = useRef(null);
  const documentFrameRef = useRef(null);
  const [signaturePad, setSignaturePad] = useState(null);
  const [fileType, setFileType] = useState('');
  const [content, setContent] = useState(null);
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
  const toast = useToast();
  const { request_id } = useParams();
  const [signed, setSigned] = useState(false);
  const [signedSignatureUrl, setSignedSignatureUrl] = useState({});
  const [isLoading, setIsLoading] = useState(false); // Added isLoading state

  const actualRequestId = signatureRequestId || request_id;

  useEffect(() => {
    const fetchData = async () => {
      const signedUrl = await cqAPI.get(`/forms/sign?request_id=${actualRequestId}`);
      setSignedSignatureUrl(signedUrl);
      if (signedUrl.type === 'error') {
        toast({
          title: 'Error',
          description: signedUrl.msg,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const response = await fetch(`${HOST}${signedUrl.data}`);
      const result = await response.json();
      if (result.type === 'error') {
        if (result.msg === 'This is already signed.') {
          setSigned(true);
          return;
        }
        toast({
          title: 'Error',
          description: result.msg,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const data = result.data;
      setFileType(data.fileType);
      setContent(Uint8Array.from(data.content.data));
    };

    fetchData();
  }, [actualRequestId, toast]);

  useEffect(() => {
    if (content && fileType === 'pdf') {
      const loadPDF = async () => {
        try {
          const loadingTask = pdfjsLib.getDocument({ data: content });
          const pdf = await loadingTask.promise;
          const canvasContainer = documentCanvasContainerRef.current;

          // Clear any existing canvases
          canvasContainer.innerHTML = '';

          // Get container width
          const containerWidth = canvasContainer.offsetWidth;

          for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1 });

            // Calculate scale based on container width
            const scale = containerWidth / viewport.width;
            const scaledViewport = page.getViewport({ scale });

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = scaledViewport.height;
            canvas.width = scaledViewport.width;

            // Set canvas style to make it responsive
            canvas.style.width = '100%';
            canvas.style.height = 'auto';

            canvasContainer.appendChild(canvas);

            const renderContext = {
              canvasContext: context,
              viewport: scaledViewport,
            };
            await page.render(renderContext).promise;
          }

          initializeSignaturePad();
        } catch (error) {
          console.error('Error loading PDF:', error);
        }
      };

      loadPDF();
    } else if (content && fileType === 'html') {
      const documentFrame = documentFrameRef.current;
      documentFrame.src = `data:text/html;base64,${btoa(
        String.fromCharCode.apply(null, content)
      )}`;
      initializeSignaturePad();
    }
  }, [content, fileType]);

  const initializeSignaturePad = () => {
    if (signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const pad = new SignaturePad(canvas);
      setSignaturePad(pad);

      // Resize the canvas to match the container
      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        pad.clear(); // otherwise isEmpty() might return incorrect value
      };

      window.addEventListener('resize', resizeCanvas);
      resizeCanvas(); // Initialize on mount

      // Add event listeners to track signature pad changes
      pad.addEventListener('beginStroke', () => {
        setIsSignatureEmpty(false);
      });

      return () => window.removeEventListener('resize', resizeCanvas);
    }
  };

  const handleClear = () => {
    if (signaturePad) {
      signaturePad.clear();
      setIsSignatureEmpty(true);
    }
  };

  const handleSave = async () => {
    setIsLoading(true); // Start loading
    try {
      const signatureData = signaturePad.toDataURL();

      const response = await fetch(`${HOST}${signedSignatureUrl.data}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature: signatureData.split(',')[1] }),
      });

      setIsSignatureEmpty(true);

      const data = await response.json();
      if (data.type === 'ok') {
        setSigned(true);
        onSuccess && onSuccess(); // Call the onSuccess callback
        return;
      }

      if (data.msg) {
        toast({
          title: 'Error',
          description: data.msg,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // End loading
    }
  };

  if (signed) {
    return (
      <Box overflowY="auto" maxHeight="100vh" width="100%" position="relative">
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100vh"
          bg="gray.100"
        >
          <Box
            p={8}
            bg="white"
            boxShadow="md"
            borderRadius="md"
            textAlign="center"
          >
            <Heading as="h2" size="lg" mb={4}>
              Thank you
            </Heading>
            <Text>This form is now signed.</Text>
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <Box p={4} overflowY="auto" width="100%" position="relative">
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading as="h1" size="lg">
          Sign Document
        </Heading>
      </Flex>
      <Box
        ref={documentCanvasContainerRef}
        style={{ border: '1px solid #000' }}
      >
        {fileType === 'html' && (
          <iframe
            ref={documentFrameRef}
            style={{ width: '100%', height: '500px' }}
          />
        )}
      </Box>
      {fileType && (
        <Box mt={4}>
          <Text mb={2}>Signature:</Text>
          <canvas
            ref={signatureCanvasRef}
            style={{
              border: '1px solid #000',
              width: '100%',
              height: '200px',
              backgroundColor: 'white',
            }}
          />
          <Flex mt={2} mb={10} justifyContent="space-between">
            <Button onClick={handleClear} colorScheme="red">
              Clear
            </Button>
            <Button
              onClick={handleSave}
              colorScheme="blue"
              isDisabled={isSignatureEmpty || isLoading}
              isLoading={isLoading}
            >
              Save
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default SignDocument;
